import type { BlockId } from '@unifyapps/defs/types/block';
import type { PropertyPanelHistoryActionProps } from '../stores/InterfaceStore/builder/actions/propertyPanelHistoryActions';
import type { InterfaceStoreType } from '../stores/InterfaceStore/builder/types';
import type { NestedPropertyData, PanelBreadcrumb, PanelViewType } from '../types/panelNavigation';

type PropertyPanelHistoryActions = InterfaceStoreType['actions']['propertyPanelHistoryActions'];

/**
 * push a single panel onto the history stack.
 * replaces 5-6 line PUSH boilerplate with a single call.
 * breadcrumb is required — the push site is responsible for resolving
 * a human-readable label at construction time.
 */
export function pushPanel(
  actions: PropertyPanelHistoryActions,
  blockId: BlockId,
  viewType: PanelViewType,
  breadcrumb: PanelBreadcrumb,
  data?: NestedPropertyData | { path: string[] } | { id: string },
) {
  // the discriminated union makes direct construction verbose,
  // so we cast through the base props type
  const props = {
    operation: 'PUSH' as const,
    payload: { blockId, viewType, data, breadcrumb },
  } as PropertyPanelHistoryActionProps;

  actions(props);
}

/**
 * pop the last panel off the history stack.
 */
export function popPanel(actions: PropertyPanelHistoryActions) {
  actions({ operation: 'POP' });
}

/**
 * clear the entire panel history stack.
 */
export function clearPanelHistory(actions: PropertyPanelHistoryActions) {
  actions({ operation: 'CLEAR' });
}

/**
 * atomically replace the full panel history stack.
 * used for deep-link navigation where the entire path is resolved at once.
 */
export function setPanelHistory(
  actions: PropertyPanelHistoryActions,
  history: InterfaceStoreType['propertyPanel']['history'],
  activeBlockId: BlockId,
) {
  actions({
    operation: 'SET_HISTORY',
    payload: { history, activeBlockId },
  });
}
